/**
 * 1.3.0 assignments' data hooks (stream P2's file): Assign for delivery…, an asset's assignment, the list.
 * Keys: queryKeys.assignment*().
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeliveryAssignmentCreateResult, DeliveryAssignmentDTO, DeliveryItemTemplate } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

/** The staff DTO (with the internal contact; the API's AssignmentDTO). */
export type AssignmentDTO = DeliveryAssignmentDTO;

export interface AssignmentChoices {
  companies: Array<{ id: string; name: string; users: Array<{ id: string; name: string; email: string; invited: boolean }> }>;
  specs: Array<{ id: string; name: string; description: string; acceptedTypes: string[]; defaultItems: DeliveryItemTemplate[] }>;
  contacts: Array<{ id: string; name: string }>;
  me: string;
}

export interface AssignmentCreateBody {
  assetIds: string[];
  companyId: string;
  portalUserId: string | null;
  specId: string | null;
  dueAt: string | null;
  timeZone: string | null;
  notes: string;
  contactUserId: string | null;
  items: DeliveryItemTemplate[];
}
export interface AssignmentPatchBody {
  portalUserId?: string | null;
  specId?: string | null;
  dueAt?: string | null;
  timeZone?: string | null;
  notes?: string;
  contactUserId?: string;
  items?: Array<DeliveryItemTemplate & { id?: string }>;
}

const choicesKey = () => [...queryKeys.assignments(), 'choices'] as const;

export function useAssignmentChoices(enabled = true) {
  return useQuery({ queryKey: choicesKey(), queryFn: () => api<AssignmentChoices>('/api/v1/assignments/choices'), enabled, staleTime: 30_000 });
}

/** The placeholder's open (or accepted) assignment, or null. */
export function useAssetAssignment(assetId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.assetAssignment(assetId), queryFn: () => api<AssignmentDTO | null>(`/api/v1/assets/${assetId}/assignment`),
    enabled: enabled && Boolean(assetId), refetchInterval: 30_000,
  });
}

export function useAssignment(id: string | null | undefined) {
  return useQuery({ queryKey: queryKeys.assignment(id), queryFn: () => api<AssignmentDTO>(`/api/v1/assignments/${id}`), enabled: Boolean(id) });
}

export function useAssignments(params: { state?: string; companyId?: string; assetId?: string; overdue?: boolean } = {}, enabled = true) {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]));
  return useQuery({
    queryKey: queryKeys.assignmentList(q.toString()), queryFn: () => api<{ items: AssignmentDTO[]; more: boolean }>(`/api/v1/assignments?${q}`),
    enabled, refetchInterval: 30_000,
  });
}

/**
 * The placeholders with an active assignment (open, in progress, in review), as assetId → overdue: the Library cards'
 * "Delivery" badge. One shared query for every card on the page.
 */
export function useActiveDeliveries(enabled = true) {
  return useQuery({
    queryKey: queryKeys.assignmentList('state=active&limit=500'),
    queryFn: async () => {
      const r = await api<{ items: AssignmentDTO[]; more: boolean }>('/api/v1/assignments?state=active&limit=500');
      return new Map(r.items.map((a) => [a.asset.id, a.overdue] as const));
    },
    enabled, refetchInterval: 60_000, staleTime: 30_000,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.assignments() });
}

export function useCreateAssignments() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: AssignmentCreateBody) => api<DeliveryAssignmentCreateResult>('/api/v1/assignments', { method: 'POST', json: body }),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAssignment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AssignmentPatchBody }) => api<AssignmentDTO>(`/api/v1/assignments/${id}`, { method: 'PATCH', json: body }),
    onSuccess: () => invalidate(),
  });
}

export function useCancelAssignment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => api<AssignmentDTO>(`/api/v1/assignments/${id}/cancel`, { method: 'POST', json: { reason: reason ?? '' } }),
    onSuccess: () => invalidate(),
  });
}
